import React from 'react';
import { Avatar, Button, Dropdown, Layout, Select, Tooltip } from 'antd';
import type { MenuProps, SelectProps } from 'antd';
import {
  DownOutlined,
  GlobalOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MoonOutlined,
  SunOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ThemeMode } from '../theme/ThemeModeContext';
import './AppHeader.css';

const { Header } = Layout;

interface AppHeaderProps {
  collapsed: boolean;
  pageTitle: string;
  language: string;
  languageLabel: string;
  languages: NonNullable<SelectProps['options']>;
  roleLabel: string;
  userName: string;
  userMenuItems: NonNullable<MenuProps['items']>;
  toggleSidebarLabel: string;
  themeMode: ThemeMode;
  themeToggleLabel: string;
  onLanguageChange: (language: string) => void;
  onToggleTheme: () => void;
  onToggleSidebar: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  collapsed,
  pageTitle,
  language,
  languageLabel,
  languages,
  roleLabel,
  userName,
  userMenuItems,
  toggleSidebarLabel,
  themeMode,
  themeToggleLabel,
  onLanguageChange,
  onToggleTheme,
  onToggleSidebar,
}) => (
  <Header className="app-header">
    <div className="app-header__title-group">
      <Button
        className="app-header__sidebar-toggle"
        type="text"
        icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
        onClick={onToggleSidebar}
        aria-label={toggleSidebarLabel}
        title={toggleSidebarLabel}
      />
      <h1 className="app-header__title" title={pageTitle}>
        {pageTitle}
      </h1>
    </div>

    <div className="app-header__controls">
      <Tooltip title={themeToggleLabel}>
        <Button
          className="app-header__theme-toggle"
          type="text"
          icon={themeMode === 'dark' ? <SunOutlined /> : <MoonOutlined />}
          onClick={onToggleTheme}
          aria-label={themeToggleLabel}
          aria-pressed={themeMode === 'dark'}
        />
      </Tooltip>

      <Select
        className="app-header__language"
        aria-label={languageLabel}
        value={language}
        onChange={onLanguageChange}
        suffixIcon={<GlobalOutlined />}
        options={languages}
      />

      <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
        <Button
          className="app-header__account"
          type="text"
          aria-label={`${userName}, ${roleLabel}`}
        >
          <Avatar className="app-header__avatar" icon={<UserOutlined />} />
          <span className="app-header__identity">
            <span className="app-header__role">{roleLabel}</span>
            <span className="app-header__name" title={userName}>{userName}</span>
          </span>
          <DownOutlined className="app-header__account-chevron" aria-hidden="true" />
        </Button>
      </Dropdown>
    </div>
  </Header>
);

export default AppHeader;
